import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Drawer, Form, Input, InputNumber, Message, Space } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import type { RulesProps } from '@arco-design/web-react/es/Form/interface';
import { cloneDeep, get } from 'lodash';

import { CheckExpandable } from '@/components/CheckExpandable';
import {
  CommandArgsNormalBlock,
  ensureCommandArgsNormalDefaults,
  initCommandApiToFormFields,
  readInitCommandFromFormPaths,
  resolveCommandArgsNormalPaths,
} from '@/delivery-type-plugin/capabilities/command-args-normal';
import { hydrateComputeResourceLeaves, readConfiguredCpuFromRecord } from '@/delivery-type-plugin/capabilities/compute';
import { ENV_VARS_ACTION_KEYS, ENV_VARS_FIELD_KEYS, EnvVarsBlock } from '@/delivery-type-plugin/capabilities/env-vars';
import {
  GPU_OPTIONS_FINGERPRINT_FIELD,
  GPU_SELECTED_ARCHITECTURE_FIELD,
  MAAS_IMAGE_CONFIRM_FIELDS,
  MAAS_NODE_DRAWER_VALIDATE_FIELD_ORDER,
  applyRegistryLeavesToForm,
  applyRegistryToGroupedNodeFields,
  buildRegistryPayload,
  pickFirstFormErrorByFieldOrder,
  readImageSourceUrl,
  readMinSharedMemoryGiFromDeployConfig,
  readRegistryAuthFromForm,
  readRegistryAuthFromValues,
  readRegistryPayloadFromForm,
  readSharedMemoryGiFromFormValue,
  resolveRegistryHost,
  runImageConfirmBusinessChecks,
  setFormFieldError,
  stampGpuOptionsFingerprint,
} from '@/delivery-type-plugin/capabilities/image';
import { maasRequiredRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import type { MaasGroupSpec, MaasInitCommand, MaasResource } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MAAS_INFERENCE_SOURCE_FIELD, MaasInferenceSource } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import {
  findGpuUsageViolation,
  formatGpuUsageValidationError,
  getCachedContainerGpuResourceConfigurations,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/gpuResourceValidation';
import {
  type InitCommandStartupFormFields,
  buildStartupFormFromDeployCommandItem,
  hasStartupInitCommandFormContent,
  hasSystemCommandArgsContent,
  initCommandApiToSystemCommandArgs,
  readGroupSystemStartupFormFields,
  startupFormToGroupSystemStartup,
  systemCommandArgsToFormFields,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/initCommandStartup';
import {
  hasGroupGpuQuantity,
  pickGroupNodeConfigForModal,
  readGroupSystemDiskSizeGi,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { INFERENCE_SYSTEM_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import type { EmbeddedNodeSpec } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  enrichEmbeddedNodeInferenceEcho,
  resolveEmbeddedHealthCheckEnabled,
  resolveEmbeddedSharedMemoryEnabled,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  isWorkerGroupNameDuplicated,
  isWorkerGroupReservedRoleName,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupValidation';
import { convertMaasResourceToContainer, isContainerStyleResource } from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import { nodeHasInferenceSystemImage } from '@/delivery-type-plugin/capabilities/maas-node-groups/submitValidation';
import { syncBuiltinInferenceImageEchoFields } from '@/delivery-type-plugin/capabilities/maas-system-config/builtinImageEcho';
import type { ContainerDeployInstanceScheduling } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigCore';
import { isContainerDeployInstanceSchedulingActive } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigCore';
import type { ContainerDeployStartupCommands } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigForm';
import { parseInferenceFrameworkImageItem } from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import { readStartupCommandForRoleFromStartupRoot } from '@/delivery-type-plugin/capabilities/maas-system-config/startupCommandsCore';
import { StartupCommandArgsBlock, resolveStartupCommandArgsPaths } from '@/delivery-type-plugin/capabilities/startup-command-args';
import { normalizeStartupArgDefinitions } from '@/delivery-type-plugin/capabilities/startup-command-args/utils';
import { readVendorDeviceMeta } from '@/delivery-type-plugin/components/vendorDevice';
import { parseContainerDeployConfigObject } from '@/delivery-type-plugin/utils/parseContainerDeployConfigObject';

import cardStyles from '../group-spec-card/group-spec-card.module.less';
import type { MaasNodeGroupsPricingOptions } from '../types';
import { GroupSpecDrawerAdvancedConfigSection, GroupSpecDrawerEmbeddedSections } from './group-spec-drawer-embedded-sections';
import styles from './group-spec-modal.module.less';
import { MaasGroupResourceSetting } from './maas-group-resource-setting';

const EMBEDDED_SERVICE_FIELD = 'resource.network.service';
const EMBEDDED_NICS_FIELD = 'resource.nics';
/** 历史误用 ComputeBlock 默认 network.nics 时的兼容读取 */
const LEGACY_EMBEDDED_NICS_FIELD = 'network.nics';

function readEmbeddedNicsFromDrawerForm(form: FormInstance): unknown {
  return (
    form.getFieldValue(EMBEDDED_NICS_FIELD) ??
    form.getFieldValue(LEGACY_EMBEDDED_NICS_FIELD) ??
    get(form.getFields() as Record<string, unknown>, EMBEDDED_NICS_FIELD) ??
    get(form.getFields() as Record<string, unknown>, LEGACY_EMBEDDED_NICS_FIELD) ??
    get(form.getFieldsValue() as Record<string, unknown>, EMBEDDED_NICS_FIELD) ??
    get(form.getFieldsValue() as Record<string, unknown>, LEGACY_EMBEDDED_NICS_FIELD)
  );
}

function readMemoryGiFromResource(resource?: Record<string, unknown>): number | undefined {
  const raw = get(resource, 'memory.request') ?? get(resource, 'memory');
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'object') {
    const request = (raw as { request?: unknown }).request;
    const num = Number(String(request ?? '').replace(/Gi$/i, ''));
    return Number.isFinite(num) ? num : undefined;
  }
  const num = Number(String(raw).replace(/Gi$/i, ''));
  return Number.isFinite(num) ? num : undefined;
}

const EMBEDDED_SYSTEM_DISK_SIZE_FIELD = '_systemDiskSize';
const EMBEDDED_SYSTEM_DISK_PRICING_FIELD = '_systemDiskPricing';

const FormItem = Form.Item;
const INIT_COMMAND_PATHS = resolveCommandArgsNormalPaths('initCommand');
const STARTUP_COMMAND_PATHS = resolveCommandArgsNormalPaths('startupCommand');
const STARTUP_INIT_COMMAND_PATHS = resolveStartupCommandArgsPaths('initCommand');

/** 归一化环境变量，过滤空项 */
function normalizeStartupEnvFromForm(raw: unknown): { name: string; value: string }[] {
  if (!Array.isArray(raw)) return [];
  return (raw as { name?: string; key?: string; value?: string }[])
    .filter((e) => e && String(e.name ?? e.key ?? '').trim())
    .map((e) => ({ name: String(e.name ?? e.key ?? ''), value: String(e.value ?? '') }));
}

function hasInitCommandValue(initCommand?: MaasInitCommand | null) {
  return !!(initCommand?.commands?.length || initCommand?.args?.length);
}

function hasEnvValue(system: Record<string, unknown> | undefined) {
  return normalizeStartupEnvFromForm(system?.env).length > 0;
}

function applyInitCommandToForm(form: ReturnType<typeof Form.useForm>[0], initCommand?: MaasInitCommand | null) {
  const fields = initCommandApiToFormFields(initCommand);
  form.setFieldValue(INIT_COMMAND_PATHS.shellType, fields._shellType);
  form.setFieldValue(INIT_COMMAND_PATHS.commands, fields._commandsText);
  form.setFieldValue(INIT_COMMAND_PATHS.args, fields._argsText);
}

function clearInitCommandForm(form: ReturnType<typeof Form.useForm>[0]) {
  form.setFieldValue(INIT_COMMAND_PATHS.shellType, undefined);
  form.setFieldValue(INIT_COMMAND_PATHS.commands, undefined);
  form.setFieldValue(INIT_COMMAND_PATHS.args, undefined);
}

function applyStartupCommandToForm(form: ReturnType<typeof Form.useForm>[0], system?: Record<string, unknown> | null) {
  const fields = systemCommandArgsToFormFields(system);
  form.setFieldValue(STARTUP_COMMAND_PATHS.shellType, fields._shellType);
  form.setFieldValue(STARTUP_COMMAND_PATHS.commands, fields._commandsText);
  form.setFieldValue(STARTUP_COMMAND_PATHS.args, fields._argsText);
}

function clearStartupCommandForm(form: ReturnType<typeof Form.useForm>[0]) {
  form.setFieldValue(STARTUP_COMMAND_PATHS.shellType, undefined);
  form.setFieldValue(STARTUP_COMMAND_PATHS.commands, undefined);
  form.setFieldValue(STARTUP_COMMAND_PATHS.args, undefined);
}

function applyStartupInitCommandToForm(form: ReturnType<typeof Form.useForm>[0], fields: InitCommandStartupFormFields) {
  form.setFieldValue(STARTUP_INIT_COMMAND_PATHS.command, fields.command);
  form.setFieldValue(STARTUP_INIT_COMMAND_PATHS.list, fields.startupArgs);
}

function clearStartupInitCommandForm(form: ReturnType<typeof Form.useForm>[0]) {
  form.setFieldValue(STARTUP_INIT_COMMAND_PATHS.command, undefined);
  form.setFieldValue(STARTUP_INIT_COMMAND_PATHS.list, undefined);
}

function readStartupInitCommandFromForm(form: ReturnType<typeof Form.useForm>[0]) {
  return {
    command: form.getFieldValue(STARTUP_INIT_COMMAND_PATHS.command),
    startupArgs: form.getFieldValue(STARTUP_INIT_COMMAND_PATHS.list),
  };
}

export type GroupRole = 'master' | 'worker';

interface GroupSpecDrawerProps extends MaasNodeGroupsPricingOptions {
  visible: boolean;
  role: GroupRole;
  initialValues?: MaasGroupSpec;
  onOk: (values: MaasGroupSpec) => void;
  onCancel: () => void;
  /** 主表单：系统盘字段写入此 form */
  parentForm?: FormInstance;
  systemDiskSizeField?: string;
  systemDiskPricingField?: string;
  systemDiskRoleLabel?: string;
  presetConfigSnapshot?: Record<string, unknown>;
  hideReplicas?: boolean;
  replicasMin?: number;
  siblingWorkers?: MaasGroupSpec[];
  editingWorkerIndex?: number;
  forbidReservedWorkerNames?: boolean;
  namespace?: string;
  isEdit?: boolean;
  inferenceSystem?: Record<string, unknown>;
  instanceScheduling?: ContainerDeployInstanceScheduling;
  peerNodes?: MaasGroupSpec[];
  baselineNodes?: MaasGroupSpec[];
  /** 初始化命令脚本提示（由 L3 注入） */
  initCommandScriptHint?: ReactNode;
  /** 初始化命令区块描述（直出文案） */
  initCommandSectionDesc?: string;
  /** 启动命令区块描述（直出文案） */
  startupCommandSectionDesc?: string;
  /** 节点 initCommand 编辑器模式，默认 script */
  initCommandEditor?: 'script' | 'startup';
  /** MI/TC：在 initCommand 之后额外展示启动命令 → system.command/args */
  showNodeStartupCommand?: boolean;
  /** 镜像 containerDeployConfig.startupCommands（hidden 字段解析） */
  imageContainerDeployStartup?: ContainerDeployStartupCommands;
  /** 新建节点组时使用的默认名称。 */
  defaultName?: string;
  /** Master 节点编辑弹窗的业务标题。 */
  masterGroupTitle?: string;
  embeddedNodeConfig?: boolean;
  showInferenceFramework?: boolean;
  showTrainingImage?: boolean;
  showService?: boolean;
  showHealthCheck?: boolean;
  showAdvancedConfig?: boolean;
  showNic?: boolean;
  customImageOnly?: boolean;
  presetArchitecture?: string;
  presetImageLocked?: boolean;
  nicPricingOptions?: Record<string, unknown>;
  requireGpuSoftware?: boolean;
}

export function GroupSpecDrawer({
  visible,
  role,
  initialValues,
  onOk,
  onCancel,
  parentForm,
  systemDiskSizeField,
  systemDiskPricingField,
  systemDiskRoleLabel,
  presetConfigSnapshot,
  hideReplicas = false,
  replicasMin = 1,
  siblingWorkers,
  editingWorkerIndex = -1,
  forbidReservedWorkerNames = false,
  namespace,
  isEdit,
  inferenceSystem,
  instanceScheduling,
  peerNodes = [],
  baselineNodes,
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  lockActionsWhenFilled,
  initCommandScriptHint,
  initCommandSectionDesc,
  startupCommandSectionDesc,
  initCommandEditor = 'script',
  showNodeStartupCommand = false,
  imageContainerDeployStartup,
  defaultName,
  masterGroupTitle,
  embeddedNodeConfig,
  showInferenceFramework,
  showTrainingImage,
  showService,
  showHealthCheck,
  showAdvancedConfig,
  showNic,
  customImageOnly,
  presetArchitecture,
  presetImageLocked,
  nicPricingOptions,
  requireGpuSoftware,
}: GroupSpecDrawerProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [showInitCommand, setShowInitCommand] = useState(false);
  const [showStartupCommand, setShowStartupCommand] = useState(false);
  const [showEnv, setShowEnv] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const imageWatchForm = showInferenceFramework ? form : (parentForm ?? form);
  const inferenceSourceWatch = Form.useWatch(MAAS_INFERENCE_SOURCE_FIELD, imageWatchForm);
  const inferenceImageWatch = Form.useWatch('inferenceFrameworkImage', imageWatchForm);
  const builtinResourceMins = useMemo(() => {
    if (inferenceSourceWatch !== MaasInferenceSource.Builtin) return {};
    const deployConfig = parseContainerDeployConfigObject(parseInferenceFrameworkImageItem(inferenceImageWatch)?.containerDeployConfig);
    return {
      minCpuCores: isContainerDeployInstanceSchedulingActive(instanceScheduling) ? instanceScheduling?.minCpuCores : undefined,
      minMemoryGi: isContainerDeployInstanceSchedulingActive(instanceScheduling) ? instanceScheduling?.minMemoryGi : undefined,
      minSharedMemoryGi: readMinSharedMemoryGiFromDeployConfig(deployConfig),
    };
  }, [inferenceImageWatch, inferenceSourceWatch, instanceScheduling]);
  const drawerInitSessionRef = useRef('');
  const isStartupInitCommandEditor = initCommandEditor === 'startup';
  const isDualCommandMode = !isStartupInitCommandEditor && showNodeStartupCommand;
  const drawerInitSessionKey = useMemo(
    () => `${visible}-${role}-${editingWorkerIndex}-${initialValues?.name ?? 'new'}`,
    [editingWorkerIndex, initialValues?.name, role, visible],
  );

  const startupFormOptions = useMemo(
    () => ({
      startupRoot: imageContainerDeployStartup as Record<string, unknown> | undefined,
      role,
    }),
    [imageContainerDeployStartup, role],
  );

  const roleDeployStartupItem = useMemo(
    () => readStartupCommandForRoleFromStartupRoot(imageContainerDeployStartup as Record<string, unknown> | undefined, role),
    [imageContainerDeployStartup, role],
  );

  const startupArgDefinitions = useMemo(() => normalizeStartupArgDefinitions(roleDeployStartupItem?.args), [roleDeployStartupItem?.args]);

  const deployPrefillFields = useMemo(
    () => buildStartupFormFromDeployCommandItem(roleDeployStartupItem, startupFormOptions),
    [roleDeployStartupItem, startupFormOptions],
  );

  const hasDeployStartupPrefill = hasStartupInitCommandFormContent(deployPrefillFields);

  const handleInitCommandExpand = useCallback(
    (checked: boolean) => {
      setShowInitCommand(checked);
      if (!checked) {
        if (isStartupInitCommandEditor) {
          clearStartupInitCommandForm(form);
        } else {
          clearInitCommandForm(form);
        }
        return;
      }
      if (isStartupInitCommandEditor) {
        if (hasDeployStartupPrefill) {
          applyStartupInitCommandToForm(form, deployPrefillFields);
        }
        return;
      }
      ensureCommandArgsNormalDefaults(form, INIT_COMMAND_PATHS);
    },
    [deployPrefillFields, form, hasDeployStartupPrefill, isStartupInitCommandEditor],
  );

  const handleStartupCommandExpand = useCallback(
    (checked: boolean) => {
      setShowStartupCommand(checked);
      if (!checked) {
        clearStartupCommandForm(form);
        return;
      }
      ensureCommandArgsNormalDefaults(form, STARTUP_COMMAND_PATHS);
    },
    [form],
  );

  useEffect(() => {
    if (!visible) {
      drawerInitSessionRef.current = '';
      setShowInitCommand(false);
      setShowStartupCommand(false);
      setShowEnv(false);
      return;
    }
    if (drawerInitSessionRef.current === drawerInitSessionKey) return;
    drawerInitSessionRef.current = drawerInitSessionKey;

    form.resetFields();
    if (initialValues) {
      const enrichedInitial = embeddedNodeConfig
        ? enrichEmbeddedNodeInferenceEcho(initialValues as EmbeddedNodeSpec)
        : (initialValues as EmbeddedNodeSpec);
      const values = cloneDeep(pickGroupNodeConfigForModal(enrichedInitial, { embeddedNodeConfig }) || enrichedInitial);
      if ((values.replicas ?? 1) < replicasMin) {
        values.replicas = replicasMin;
      }
      if (values.resource && !isContainerStyleResource(values.resource as unknown as Record<string, unknown>)) {
        values.resource = convertMaasResourceToContainer(values.resource as MaasResource) as unknown as MaasGroupSpec['resource'];
      }
      const embeddedInitial = values as EmbeddedNodeSpec;
      form.setFieldsValue({
        name: values.name,
        replicas: values.replicas,
        resource: values.resource,
        system: values.system,
        ...(embeddedNodeConfig
          ? {
              _containerDeployHealthCheck: embeddedInitial._containerDeployHealthCheck,
              _containerDeploySharedMemory: embeddedInitial._containerDeploySharedMemory,
              _healthCheckEnabled: embeddedInitial._healthCheckEnabled ?? resolveEmbeddedHealthCheckEnabled(embeddedInitial),
              _sharedMemoryEnabled: embeddedInitial._sharedMemoryEnabled ?? resolveEmbeddedSharedMemoryEnabled(embeddedInitial),
              _nicEnabled: embeddedInitial._nicEnabled,
              _systemDiskSize: embeddedInitial._systemDiskSize ?? readGroupSystemDiskSizeGi(initialValues),
              _systemDiskPricing: embeddedInitial._systemDiskPricing,
              maasInferenceSource: embeddedInitial.maasInferenceSource,
              inferenceFrameworkImage: embeddedInitial.inferenceFrameworkImage,
              framework: (embeddedInitial.system as Record<string, unknown> | undefined)?.framework,
            }
          : {}),
      });
      hydrateComputeResourceLeaves(form, values.resource as Record<string, unknown> | undefined, {
        cpuRequest: 'resource.cpu.request',
        memory: 'resource.memory.request',
        gpu: 'resource.gpu',
      });
      const hydratedRegistry = (get(values, 'system.registry') as MaasGroupSpec['registry']) ?? values.registry;
      applyRegistryLeavesToForm(form, hydratedRegistry);
      if (embeddedNodeConfig && hydratedRegistry && (hydratedRegistry.username || hydratedRegistry.password)) {
        form.setFieldValue('registry.insecureTLS', true);
      }
      const hydratedArch = String(get(values, 'system.architecture') ?? '').trim();
      if (hydratedArch) {
        form.setFieldValue('system.architecture', hydratedArch);
      }
      const hydratedGpu = get(values, 'resource.gpu');
      if (hydratedArch && hydratedGpu && typeof hydratedGpu === 'object') {
        form.setFieldValue(GPU_SELECTED_ARCHITECTURE_FIELD, hydratedArch);
        void stampGpuOptionsFingerprint(form, hydratedArch);
      }
      if (embeddedNodeConfig && embeddedInitial.system) {
        syncBuiltinInferenceImageEchoFields(
          form,
          'system',
          embeddedInitial.system as Record<string, unknown>,
          embeddedInitial.inferenceFrameworkImage,
        );
      }
      if (isStartupInitCommandEditor) {
        const startupFields = readGroupSystemStartupFormFields(values, startupFormOptions);
        if (hasStartupInitCommandFormContent(startupFields)) {
          applyStartupInitCommandToForm(form, startupFields);
          setShowInitCommand(true);
        } else if (hasDeployStartupPrefill) {
          applyStartupInitCommandToForm(form, deployPrefillFields);
          setShowInitCommand(true);
        } else {
          setShowInitCommand(false);
        }
      } else {
        applyInitCommandToForm(form, values.initCommand);
        setShowInitCommand(hasInitCommandValue(values.initCommand));
        if (isDualCommandMode) {
          const groupSystem = values.system as Record<string, unknown> | undefined;
          applyStartupCommandToForm(form, groupSystem);
          setShowStartupCommand(hasSystemCommandArgsContent(groupSystem));
        }
      }
      setShowEnv(hasEnvValue(initialValues.system as Record<string, unknown> | undefined));
    } else {
      const defaultReplicas = hideReplicas ? 1 : replicasMin;
      form.setFieldsValue({ replicas: defaultReplicas, name: defaultName || role || '' });
      if (isStartupInitCommandEditor && hasDeployStartupPrefill) {
        applyStartupInitCommandToForm(form, deployPrefillFields);
        setShowInitCommand(true);
      } else {
        setShowInitCommand(false);
      }
      setShowStartupCommand(false);
      setShowEnv(false);
    }
  }, [
    deployPrefillFields,
    hasDeployStartupPrefill,
    initialValues,
    form,
    hideReplicas,
    defaultName,
    isDualCommandMode,
    isStartupInitCommandEditor,
    replicasMin,
    role,
    startupFormOptions,
    drawerInitSessionKey,
    embeddedNodeConfig,
    visible,
  ]);

  const nameRules = useMemo<RulesProps[]>(() => {
    const rules: RulesProps[] = [...maasRequiredRules(t('common:name'))];
    if (role === 'worker') {
      rules.push({
        validator(value: string, callback: (error?: string) => void) {
          if (forbidReservedWorkerNames && isWorkerGroupReservedRoleName(value)) {
            callback(t('data-center:maas.worker.group.name.reserved'));
            return;
          }
          if (siblingWorkers?.length && isWorkerGroupNameDuplicated(siblingWorkers, value, editingWorkerIndex)) {
            callback(t('data-center:maas.worker.group.name.duplicate'));
            return;
          }
          callback();
        },
      });
    }
    return rules;
  }, [forbidReservedWorkerNames, role, siblingWorkers, editingWorkerIndex, t]);

  const handleOk = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      if (embeddedNodeConfig && (showInferenceFramework || showTrainingImage)) {
        const imageValues = form.getFieldsValue() as Record<string, unknown>;
        const imageSystem = parseFormJsonField<Record<string, unknown>>(form.getFieldValue('system'));
        const sourceUrl =
          readImageSourceUrl(imageValues, ['system.image', 'system.url']) || String(imageSystem?.image ?? imageSystem?.url ?? '').trim();
        if (!sourceUrl) {
          const inferenceSource = form.getFieldValue(MAAS_INFERENCE_SOURCE_FIELD) as MaasInferenceSource | undefined;
          const sourceUrlField = showTrainingImage
            ? 'system.image'
            : inferenceSource === MaasInferenceSource.Custom
              ? 'system.url'
              : MAAS_IMAGE_CONFIRM_FIELDS.sourceUrl;
          setFormFieldError(
            form,
            sourceUrlField,
            showTrainingImage ? t('data-center:please.enter.image') : t('data-center:maas.inference.image.required'),
          );
          return;
        }
      }
      if (showService && embeddedNodeConfig) {
        await form.validate([EMBEDDED_SERVICE_FIELD]);
      }
      const nicsBeforeValidate = readEmbeddedNicsFromDrawerForm(form);
      if (showNic && embeddedNodeConfig && Array.isArray(nicsBeforeValidate) && nicsBeforeValidate.length > 0) {
        await form.validate([EMBEDDED_NICS_FIELD, LEGACY_EMBEDDED_NICS_FIELD]);
      }
      await form.validate();
      const systemDiskForm = embeddedNodeConfig ? form : parentForm;
      const effectiveSystemDiskField = embeddedNodeConfig ? EMBEDDED_SYSTEM_DISK_SIZE_FIELD : systemDiskSizeField;
      if (systemDiskForm && effectiveSystemDiskField) {
        try {
          await systemDiskForm.validate([effectiveSystemDiskField]);
        } catch (e) {
          console.log((e as { errors?: Record<string, unknown> }).errors ?? e);
          return;
        }
      }
      /** getFieldsValue 可能漏未完全挂载的叶子；用完整 store 补齐计价厂商设备 */
      const nested = form.getFieldsValue() as Record<string, unknown>;
      const store = form.getFields() as Record<string, unknown>;
      const resource = {
        ...((nested.resource as Record<string, unknown> | undefined) || {}),
      };
      const cpuPricing = readVendorDeviceMeta(store, 'resource.cpuPricing') || readVendorDeviceMeta(nested, 'resource.cpuPricing');
      const memoryPricing = readVendorDeviceMeta(store, 'resource.memoryPricing') || readVendorDeviceMeta(nested, 'resource.memoryPricing');
      const gpuMeta = readVendorDeviceMeta(store, 'resource.gpu') || readVendorDeviceMeta(nested, 'resource.gpu');
      if (cpuPricing) resource.cpuPricing = cpuPricing;
      if (memoryPricing) resource.memoryPricing = memoryPricing;
      if (gpuMeta) {
        resource.gpu = {
          ...((resource.gpu as Record<string, unknown> | undefined) || {}),
          ...gpuMeta,
          ...(get(nested, 'resource.gpu.number') != null ? { number: get(nested, 'resource.gpu.number') } : {}),
          ...(get(store, 'resource.gpu.number') != null ? { number: get(store, 'resource.gpu.number') } : {}),
        };
      }
      const embeddedNics = readEmbeddedNicsFromDrawerForm(form);
      const embeddedNicEnabled = form.getFieldValue('_nicEnabled') ?? store._nicEnabled ?? nested._nicEnabled;
      if (embeddedNodeConfig && embeddedNics !== undefined) {
        resource.nics = embeddedNics;
      }
      const embeddedSystem = (store.system ?? nested.system) as Record<string, unknown> | undefined;
      const drawerRegistry = readRegistryPayloadFromForm(form) ?? (parentForm ? readRegistryPayloadFromForm(parentForm) : undefined);
      const systemWithRegistry = {
        ...(embeddedSystem || {}),
        ...(drawerRegistry ? { registry: drawerRegistry } : {}),
      };
      const payload = pickGroupNodeConfigForModal(
        {
          ...nested,
          ...(Object.keys(systemWithRegistry).length ? { system: systemWithRegistry } : {}),
          resource,
          ...(embeddedNodeConfig
            ? {
                _containerDeployHealthCheck: store._containerDeployHealthCheck ?? nested._containerDeployHealthCheck,
                _containerDeploySharedMemory: store._containerDeploySharedMemory ?? nested._containerDeploySharedMemory,
                _healthCheckEnabled: store._healthCheckEnabled ?? nested._healthCheckEnabled,
                _sharedMemoryEnabled: store._sharedMemoryEnabled ?? nested._sharedMemoryEnabled,
                _nicEnabled: embeddedNicEnabled ?? (Array.isArray(embeddedNics) && embeddedNics.length > 0 ? true : nested._nicEnabled),
                _systemDiskSize: store._systemDiskSize ?? nested._systemDiskSize,
                _systemDiskPricing: store._systemDiskPricing ?? nested._systemDiskPricing,
                maasInferenceSource: store.maasInferenceSource ?? nested.maasInferenceSource,
                inferenceFrameworkImage: store.inferenceFrameworkImage ?? nested.inferenceFrameworkImage,
              }
            : {}),
        } as unknown as MaasGroupSpec,
        { embeddedNodeConfig },
      ) as MaasGroupSpec;
      if (embeddedNodeConfig && (showInferenceFramework || showTrainingImage) && !nodeHasInferenceSystemImage(payload)) {
        Message.error(t('data-center:maas.inference.image.required'));
        return;
      }
      if (
        payload.resource?.gpu &&
        !hasGroupGpuQuantity(payload.resource as Record<string, unknown>) &&
        !get(payload.resource, 'gpu.vendor') &&
        !get(payload.resource, 'gpu.vendorID') &&
        !get(payload.resource, 'gpu.resourceType')
      ) {
        delete payload.resource.gpu;
      }
      const gpuResourceConfigs = await getCachedContainerGpuResourceConfigurations();
      const gpuViolation = findGpuUsageViolation([...peerNodes, payload], gpuResourceConfigs, { baselineNodes });
      if (gpuViolation) {
        Message.error(formatGpuUsageValidationError(t, gpuViolation));
        return;
      }

      if (showInitCommand) {
        if (isStartupInitCommandEditor) {
          const { command, startupArgs } = readStartupInitCommandFromForm(form);
          const startup = startupFormToGroupSystemStartup(command, startupArgs);
          const system = { ...((payload.system as Record<string, unknown> | undefined) || {}), ...startup };
          if (!startup.command?.length) delete system.command;
          if (!startup.args?.length) delete system.args;
          payload.system = Object.keys(system).length ? system : undefined;
          delete payload.initCommand;
        } else {
          const initCommand = readInitCommandFromFormPaths(form, INIT_COMMAND_PATHS);
          if (initCommand) payload.initCommand = initCommand;
          else delete payload.initCommand;
        }
      } else {
        delete payload.initCommand;
        if (isStartupInitCommandEditor && payload.system) {
          const system = { ...(payload.system as Record<string, unknown>) };
          delete system.command;
          delete system.args;
          payload.system = Object.keys(system).length ? system : undefined;
        }
      }

      if (isDualCommandMode) {
        const system = { ...((payload.system as Record<string, unknown> | undefined) || {}) };
        if (showStartupCommand) {
          const startup = initCommandApiToSystemCommandArgs(readInitCommandFromFormPaths(form, STARTUP_COMMAND_PATHS));
          if (startup.command?.length) system.command = startup.command;
          else delete system.command;
          if (startup.args?.length) system.args = startup.args;
          else delete system.args;
        } else {
          delete system.command;
          delete system.args;
        }
        payload.system = Object.keys(system).length ? system : undefined;
      }

      if (payload.system) {
        const system = { ...payload.system } as Record<string, unknown>;
        if (!showEnv) {
          delete system.env;
        } else {
          const env = normalizeStartupEnvFromForm(system.env);
          if (env.length) system.env = env;
          else delete system.env;
        }
        const rootFramework = store.framework ?? nested.framework;
        if (rootFramework) {
          system.framework = rootFramework;
        }
        payload.system = Object.keys(system).length ? system : undefined;
      }

      const shouldProbeImage = showTrainingImage || (!customImageOnly && !showTrainingImage);
      if (shouldProbeImage) {
        const imageOnDrawer = Boolean(showInferenceFramework || showTrainingImage);
        const imageForm = imageOnDrawer ? form : (parentForm ?? form);
        const imageValues = imageForm.getFieldsValue() as Record<string, unknown>;
        const imageSystem = parseFormJsonField<Record<string, unknown>>(
          imageForm.getFieldValue(imageOnDrawer ? 'system' : INFERENCE_SYSTEM_FIELD),
        );
        const sourceUrl =
          readImageSourceUrl(imageValues, [
            imageOnDrawer ? 'system.image' : `${INFERENCE_SYSTEM_FIELD}.image`,
            imageOnDrawer ? 'system.url' : `${INFERENCE_SYSTEM_FIELD}.url`,
          ]) || String(imageSystem?.image ?? imageSystem?.url ?? '').trim();
        const registryAuth = imageOnDrawer
          ? readRegistryAuthFromForm(imageForm, 'registry', { authSwitch: true })
          : readRegistryAuthFromValues(imageValues);
        const architecture = String(get(payload, 'system.architecture') ?? form.getFieldValue('system.architecture') ?? '').trim();
        const inferenceSource = imageForm.getFieldValue(MAAS_INFERENCE_SOURCE_FIELD) as MaasInferenceSource | undefined;
        const isBuiltin = !showTrainingImage && inferenceSource === MaasInferenceSource.Builtin;
        const imageItem = parseInferenceFrameworkImageItem(imageForm.getFieldValue('inferenceFrameworkImage'));
        const deployConfig = parseContainerDeployConfigObject(imageItem?.containerDeployConfig);
        const sharedMemoryGi = readSharedMemoryGiFromFormValue(
          form.getFieldValue('_containerDeploySharedMemory') ?? store._containerDeploySharedMemory,
        );
        const sourceUrlField = showTrainingImage
          ? 'system.image'
          : showInferenceFramework && inferenceSource === MaasInferenceSource.Custom
            ? 'system.url'
            : MAAS_IMAGE_CONFIRM_FIELDS.sourceUrl;
        const emptySourceMessage = showTrainingImage
          ? t('data-center:please.enter.image')
          : showInferenceFramework
            ? t('data-center:maas.inference.image.required')
            : t('data-center:please.enter.image');
        const checked = await runImageConfirmBusinessChecks({
          sourceUrl,
          registryAuth,
          architecture,
          gpu: payload.resource?.gpu,
          selectedGpuArchitecture: String(form.getFieldValue(GPU_SELECTED_ARCHITECTURE_FIELD) ?? '').trim(),
          selectedGpuOptionsFingerprint: String(form.getFieldValue(GPU_OPTIONS_FINGERPRINT_FIELD) ?? '').trim(),
          isBuiltin,
          minCpuCores: isContainerDeployInstanceSchedulingActive(instanceScheduling) ? instanceScheduling?.minCpuCores : undefined,
          minMemoryGi: isContainerDeployInstanceSchedulingActive(instanceScheduling) ? instanceScheduling?.minMemoryGi : undefined,
          minSharedMemoryGi: isBuiltin && embeddedNodeConfig ? readMinSharedMemoryGiFromDeployConfig(deployConfig) : undefined,
          cpu: readConfiguredCpuFromRecord(payload.resource?.cpu),
          memoryGi: readMemoryGiFromResource(payload.resource as Record<string, unknown> | undefined),
          sharedMemoryGi,
          fields: {
            ...MAAS_IMAGE_CONFIRM_FIELDS,
            sourceUrl: imageOnDrawer ? sourceUrlField : `${INFERENCE_SYSTEM_FIELD}.image`,
          },
          emptySourceMessage,
          t,
        });
        if (checked.ok === false) {
          const errorForm =
            checked.field && (checked.field.startsWith(INFERENCE_SYSTEM_FIELD) || checked.field.startsWith('basic.image'))
              ? imageForm
              : form;
          setFormFieldError(errorForm, checked.field, checked.message);
          return;
        }
        const registry = buildRegistryPayload(resolveRegistryHost(checked.registryHost, sourceUrl), registryAuth);
        if (registry) {
          const system = { ...((payload.system as Record<string, unknown> | undefined) || {}), registry };
          payload.system = system;
          delete payload.registry;
          applyRegistryLeavesToForm(form, registry);
          if (parentForm) {
            applyRegistryToGroupedNodeFields(parentForm, registry);
          } else {
            applyRegistryToGroupedNodeFields(form, registry);
          }
        }
      } else if (showInferenceFramework || showTrainingImage) {
        const registry = readRegistryPayloadFromForm(form) ?? (parentForm ? readRegistryPayloadFromForm(parentForm) : undefined);
        if (registry) {
          payload.system = { ...((payload.system as Record<string, unknown> | undefined) || {}), registry };
          delete payload.registry;
        }
      }

      onOk(payload);
    } catch (e) {
      const message = pickFirstFormErrorByFieldOrder(e, MAAS_NODE_DRAWER_VALIDATE_FIELD_ORDER);
      if (message) {
        Message.error(message);
      } else {
        console.log((e as { errors?: Record<string, unknown> }).errors ?? e);
      }
    } finally {
      setConfirming(false);
    }
  };

  const renderOptionalCard = (
    label: string,
    checked: boolean,
    onChange: (checked: boolean) => void,
    content: ReactNode,
    sectionDesc?: string,
  ) => (
    <Card className={styles.configCard} bordered={false}>
      <CheckExpandable
        label={label}
        checked={checked}
        onChange={onChange}
        contentStyle={{ paddingTop: 8 }}
        persistentContent={sectionDesc ? <div className={styles.commandSectionDesc}>{sectionDesc}</div> : undefined}
      >
        {content}
      </CheckExpandable>
    </Card>
  );

  const initCommandCardLabel = isStartupInitCommandEditor
    ? t('components:startup.command.and.args')
    : t('data-center:maas.init.command.and.args');

  const drawerTitle = role === 'master' ? (masterGroupTitle ?? t('data-center:maas.master.group')) : t('data-center:maas.worker.group');

  return (
    <Drawer
      visible={visible}
      title={drawerTitle}
      width={800}
      unmountOnExit
      focusLock={false}
      autoFocus={false}
      className={styles.groupSpecDrawer}
      maskClosable={false}
      onCancel={onCancel}
      footer={
        <Space>
          <Button onClick={onCancel} disabled={confirming}>
            {t('common:cancel')}
          </Button>
          <Button type="primary" onClick={handleOk} loading={confirming} disabled={confirming}>
            {t('common:confirm')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <FormItem label={t('common:name')} field="name" rules={nameRules}>
          <Input />
        </FormItem>
        {!hideReplicas && (
          <FormItem
            label={t('data-center:maas.node.count')}
            field="replicas"
            rules={maasRequiredRules(t('data-center:maas.node.count'))}
            initialValue={replicasMin}
          >
            <InputNumber min={replicasMin} precision={0} mode="button" />
          </FormItem>
        )}
        {hideReplicas && (
          <FormItem field="replicas" initialValue={replicasMin} hidden>
            <InputNumber />
          </FormItem>
        )}
        <GroupSpecDrawerEmbeddedSections
          form={form}
          parentForm={parentForm}
          embeddedNodeConfig={embeddedNodeConfig}
          showInferenceFramework={showInferenceFramework}
          showTrainingImage={showTrainingImage}
          customImageOnly={customImageOnly}
          isEdit={isEdit}
          presetArchitecture={presetArchitecture}
          presetImageLocked={presetImageLocked}
          lockActionsWhenFilled={lockActionsWhenFilled}
        />
        <MaasGroupResourceSetting
          form={form}
          parentForm={parentForm}
          namespace={namespace}
          isEdit={isEdit}
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          paymentTiming={paymentTiming}
          skuType={skuType}
          resourceEditMode={resourceEditMode}
          lockActionsWhenFilled={lockActionsWhenFilled}
          embeddedNodeConfig={embeddedNodeConfig}
          showNic={showNic}
          systemDiskSizeField={systemDiskSizeField}
          systemDiskPricingField={systemDiskPricingField}
          presetConfigSnapshot={presetConfigSnapshot}
          nicPricingOptions={nicPricingOptions}
          minCpuCores={builtinResourceMins.minCpuCores}
          minMemoryGi={builtinResourceMins.minMemoryGi}
          minSharedMemoryGi={builtinResourceMins.minSharedMemoryGi}
          requireGpuSoftware={requireGpuSoftware}
        />
        <div className={`${styles.optionalList} ${cardStyles.optionalBlock}`}>
          {renderOptionalCard(
            initCommandCardLabel,
            showInitCommand,
            handleInitCommandExpand,
            showInitCommand ? (
              isStartupInitCommandEditor ? (
                <StartupCommandArgsBlock
                  form={form}
                  paths={STARTUP_INIT_COMMAND_PATHS}
                  blockMode="edit"
                  options={{ noStyle: false, argDefinitions: startupArgDefinitions }}
                />
              ) : (
                <CommandArgsNormalBlock
                  form={form}
                  paths={INIT_COMMAND_PATHS}
                  blockMode="edit"
                  options={{ noStyle: false, scriptHint: initCommandScriptHint }}
                />
              )
            ) : null,
            initCommandSectionDesc,
          )}

          {isDualCommandMode
            ? renderOptionalCard(
                t('components:startup.command.and.args'),
                showStartupCommand,
                handleStartupCommandExpand,
                showStartupCommand ? (
                  <CommandArgsNormalBlock
                    form={form}
                    paths={STARTUP_COMMAND_PATHS}
                    blockMode="edit"
                    options={{ noStyle: false, scriptHint: initCommandScriptHint }}
                  />
                ) : null,
                startupCommandSectionDesc,
              )
            : null}

          {renderOptionalCard(
            t('common:environment'),
            showEnv,
            (checked) => {
              setShowEnv(checked);
              if (!checked) {
                form.setFieldValue('system.env', undefined);
              }
            },
            <EnvVarsBlock
              form={form}
              paths={{ list: 'system.env' }}
              options={{ noStyle: true }}
              blockMode="edit"
              fieldModes={{
                [ENV_VARS_FIELD_KEYS.list]: 'editable',
              }}
              actionModes={{
                [ENV_VARS_ACTION_KEYS.add]: 'enabled',
                [ENV_VARS_ACTION_KEYS.remove]: 'enabled',
              }}
              title="公共环境变量"
            />,
          )}

          <GroupSpecDrawerAdvancedConfigSection
            form={form}
            embeddedNodeConfig={embeddedNodeConfig}
            showAdvancedConfig={showAdvancedConfig ?? showHealthCheck}
          />
        </div>
      </Form>
    </Drawer>
  );
}

/** @deprecated 使用 GroupSpecDrawer；保留别名兼容旧引用 */
export const GroupSpecModal = GroupSpecDrawer;
